//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
export enum EcommerceProvider {
  NotApplicable = 'NotApplicable',
  CuralatePixel = 'CuralatePixel',
  AdobeOmniture = 'AdobeOmniture',
  GoogleAnalytics = 'GoogleAnalytics',
  BvPixel = 'BvPixel',
  Demo = 'Demo',
}

export interface IStatsRequest {
  interval: string;
  ecommerceSessionType: string;
  sourceType?: string;
  testVariation?: string;
  sourceId?: string;
  displayType?: string;
  labels?: string[];
}

export interface IBenchmarkStatsRequest {
  sourceType: string;
  sourceId: string;
  displayType: string;
}

export enum UnifiedMetricsSortDirection {
  asc = 'asc',
  desc = 'desc',
}

export enum UnifiedMetricsSortKey {
  Views = 'Views',
  InteractiveViews = 'InteractiveViews',
  ExternalClicks = 'ExternalClicks',
  ClickThroughRate = 'ClickThroughRate',
  TaggedMedia = 'TaggedMedia',
  Followers = 'Followers',
  Revenue = 'Revenue',
  ConversionRate = 'ConversionRate',
  EcommerceRevenue = 'EcommerceRevenue',
  EcommerceTransactions = 'EcommerceTransactions',
  EcommerceSessions = 'EcommerceSessions',
  PageImpressions = 'PageImpressions',
  PageImpressionsNoContent = 'PageImpressionsNoContent',
}

export interface IItemProductStatsRequest {
  interval: string;
  ecommerceSessionType: string;
  sourceType?: string;
  testVariation?: string;
  sortKey?: UnifiedMetricsSortKey;
  sortDirection?: UnifiedMetricsSortDirection;
  limit?: number;
  offset?: number;
  labels?: string[];
}

export interface ILabelsRequest {
  interval: string;
  sourceType?: string;
  testVariation?: string;
}

export interface ILabelsResponse {
  label: string;
  count: number;
}

export interface IItemProductStatsRequestWithContainer
  extends IItemProductStatsRequest {
  containerId?: string;
  excludeContainerId?: boolean;
}

export interface IEmailStatsRequest {
  interval: string;
}

export interface IContentAcquisitionRequest {
  interval: string;
}

export interface IMetadataResponse {
  ecommerceProvider: EcommerceProvider;
  ecommerceTracking: boolean;
  revenueAttributionModel: string;
  validTouchpoints: string[];
  createdOn?: number;
  timezone: string;
  currency?: string;
}

export interface IBubbleStatsResponse {
  visits: number | string;
  interactions: number | string;
  clicks: number | string;
  transactions: number | string;
  revenue: number | string;
  clickThroughRate: number | string;
  interactionRate: number | string;
  conversionRate: number | string;
  averageOrderValue: number | string;
}

interface IBaseGalleryStats {
  revenue: number | string;
  transactions: number | string;
  averageOrderValue: number | string;
  visits: number | string;
  interactions: number | string;
  clicks: number | string;
  clickThroughRate: number | string;
  conversionRate: number | string;
}

export interface IDisplayTypeStats extends IBaseGalleryStats {
  display: string;
}

export interface ISourceIdStats extends IBaseGalleryStats {
  sourceId: string;
  display: string;
}

export interface ITouchpointStatsResponse {
  touchpoint: string;
  displayTypeStats: IDisplayTypeStats[];
  sourceIdStats: ISourceIdStats[];
  revenue: number | string;
  transactions: number | string;
  averageOrderValue: number | string;
  visits: number | string;
  interactions: number | string;
  clicks: number | string;
  clickThroughRate: number | string;
  conversionRate: number | string;
}

export interface IGraphStatsResponse {
  touchpoint: string;
  timestamp: number;
  dateTimeIdentifier: string;
  visitors: number;
  revenue: number;
  visits: number;
  clicks: number;
  transactions: number;
  interactionRate: number;
  clickThroughRate: number;
  conversionRate: number;
  pageImpressionCoverage?: number;
}

interface ILiftStat {
  curalateStat: number;
  nonCuralateStat: number;
  lift: number;
}

export interface ILiftStatsResponse {
  timeOnSite: ILiftStat;
  conversionRate: ILiftStat;
  averageOrderValue: ILiftStat;
}

export interface IClientBenchmarkStat {
  value: number;
  percentile: number;
}

export interface IClientBenchmarkStats {
  conversionRateLift: IClientBenchmarkStat;
  aovLift: IClientBenchmarkStat;
  timeOnSiteLift: IClientBenchmarkStat;
  interactionRate: IClientBenchmarkStat;
  clickthroughRate: IClientBenchmarkStat;
}

export interface IBenchmarkPercentiles {
  percentiles: number[];
}

export interface IRelatedClientBenchmarkPercentiles {
  conversionRateLiftPercentiles: IBenchmarkPercentiles;
  aovLiftPercentiles: IBenchmarkPercentiles;
  timeOnSiteLiftPercentiles: IBenchmarkPercentiles;
  interactionRatePercentiles: IBenchmarkPercentiles;
  clickthroughRatePercentiles: IBenchmarkPercentiles;
}

export interface IBenchmarkStatsResponse {
  clientHasValidFanreelTraffic: boolean;
  isPixelClient: boolean;
  clientStats: IClientBenchmarkStats;
  relatedClientStats: IRelatedClientBenchmarkPercentiles;
}

interface INetworkInfo {
  reach: number;
  followers: number;
  network: string;
  engagementRate: number;
  username: string;
  userProfileUrl: string;
  userProfileImageUrl: string;
  userProfileImageFileId: string;
  emailHash: string;
}

interface IItemInfo {
  deetsItemId: string;
  cuid: string;
  fileId: string;
  timePosted: number;
  networkInfo: INetworkInfo;
}

export interface IItemStatsResponse {
  mediaInfo: IItemInfo;
  views: number;
  interactions: number;
  clicks: number;
  clickThroughRate: number;
  revenue: number;
  transactions: number;
  conversionRate: number;
  ecommerceSessions: number;
  totalCount: number;
}

interface IProductInfo {
  productName: string;
  productUrl: string;
  productPrice: number;
  fileId: string;
}

export interface IProductStatsResponse {
  mediaInfo: IProductInfo;
  views: number;
  interactions: number;
  clicks: number;
  clickThroughRate: number;
  revenue: number;
  transactions: number;
  conversionRate: number;
  ecommerceSessions: number;
  taggedMedia: number;
  totalCount: number;
  pageImpressions?: number;
  pageImpressionNoContents?: number;
}

export interface IContributorStatsResponse {
  mediaInfo: IContributorInfo;
  views: number;
  interactions: number;
  clicks: number;
  clickThroughRate: number;
  revenue: number;
  transactions: number;
  conversionRate: number;
  ecommerceSessions: number;
  totalCount: number;
}

interface IContributorInfo {
  networkInfo: INetworkInfo;
}

export interface IEmailsCountResponse {
  emailCount: number;
}

export interface IEmailEnabledResponse {
  enabled: boolean;
}

export interface IContentAcquisitionResponse {
  distributedCount: number;
  ownedCount: number;
  acquiredCount: number;
}
